import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Plus, X } from "lucide-react";

const EMPTY = { part_code: "", description: "", hsn_code: "", category: "", uom: "Nos", gst_rate: 18, notes: "" };

export default function Items() {
    const { hasPerm } = useAuth();
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [show, setShow] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [err, setErr] = useState("");

    const load = async () => { const { data } = await api.get("/items"); setItems(data); };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault(); setErr("");
        try {
            const payload = { ...form, gst_rate: Number(form.gst_rate) || 0 };
            if (editId) await api.put(`/items/${editId}`, payload); else await api.post("/items", payload);
            setShow(false); setForm(EMPTY); setEditId(null); load();
        } catch (err) { setErr(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    };

    const filtered = items.filter(i => (i.part_code + " " + i.description).toLowerCase().includes(q.toLowerCase()));

    return (
        <div data-testid="items-page" className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">Master data</div>
                    <h1 className="text-3xl font-heading font-semibold text-stone-900">Item catalog</h1>
                </div>
                {hasPerm("item.manage") && <button data-testid="new-item-btn" onClick={() => { setForm(EMPTY); setEditId(null); setShow(true); }} className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2"><Plus size={15} /> New item</button>}
            </div>

            {!hasPerm("item.manage") && (
                <div className="bg-stone-100 border border-stone-200 rounded-xl p-4 text-sm text-stone-700">
                    <span className="font-medium">Read-only view.</span> The item catalog is managed by the Administrator and Full Access users.
                </div>
            )}

            <div>
                <input placeholder="Search part code or description…" value={q} onChange={(e) => setQ(e.target.value)}
                       className="w-full md:w-80 border border-stone-300 rounded-md text-sm py-2 px-3" />
            </div>

            <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
                            <th className="text-left py-3 px-4">Part code</th>
                            <th className="text-left py-3 px-4">Description</th>
                            <th className="text-left py-3 px-4">HSN/SAC</th>
                            <th className="text-left py-3 px-4">Category</th>
                            <th className="text-left py-3 px-4">UOM</th>
                            <th className="text-right py-3 px-4">GST %</th>
                            <th className="text-right py-3 px-4"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(i => (
                            <tr key={i.id} className="border-t border-stone-100 hover:bg-stone-50">
                                <td className="py-3 px-4 font-mono text-xs text-stone-800">{i.part_code}</td>
                                <td className="py-3 px-4 text-stone-800">{i.description}</td>
                                <td className="py-3 px-4 text-stone-600 text-xs">{i.hsn_code || "—"}</td>
                                <td className="py-3 px-4 text-stone-700">{i.category || "—"}</td>
                                <td className="py-3 px-4 text-stone-700">{i.uom}</td>
                                <td className="py-3 px-4 text-right text-stone-700">{i.gst_rate}%</td>
                                <td className="py-3 px-4 text-right">
                                    {hasPerm("item.manage") && <button onClick={() => { setForm(i); setEditId(i.id); setShow(true); }} className="text-xs text-amber-700 hover:text-amber-900">Edit</button>}
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-stone-500 text-sm">{q ? "No items match." : "No items yet."}</td></tr>}
                    </tbody>
                </table>
            </div>

            {show && (
                <div className="fixed inset-0 z-40 bg-stone-900/40 flex items-center justify-center p-4">
                    <div className="bg-white border border-stone-200 rounded-xl max-w-2xl w-full">
                        <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
                            <div className="font-heading font-semibold text-stone-900">{editId ? "Edit item" : "New item"}</div>
                            <button onClick={() => setShow(false)} className="text-stone-500"><X size={18} /></button>
                        </div>
                        <form onSubmit={submit} className="p-6 space-y-4">
                            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="overline block mb-1">Part code *</label>
                                    <input data-testid="item-code" required value={form.part_code} onChange={(e) => setForm({ ...form, part_code: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3 font-mono" disabled={!!editId} />
                                </div>
                                <div>
                                    <label className="overline block mb-1">HSN/SAC</label>
                                    <input value={form.hsn_code} onChange={(e) => setForm({ ...form, hsn_code: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3 font-mono" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="overline block mb-1">Description *</label>
                                    <input data-testid="item-desc" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" />
                                </div>
                                <div>
                                    <label className="overline block mb-1">Category</label>
                                    <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" />
                                </div>
                                <div>
                                    <label className="overline block mb-1">UOM</label>
                                    <input value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" />
                                </div>
                                <div>
                                    <label className="overline block mb-1">GST %</label>
                                    <input type="number" min="0" step="0.1" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => setShow(false)} className="text-sm text-stone-600 px-4 py-2 rounded-md">Cancel</button>
                                <button data-testid="save-item" type="submit" className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm font-medium">Save</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
